import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Cookies from "js-cookie";
import { AlertTriangle, Loader2, LogOut, Eye, EyeOff, RefreshCw } from "lucide-react";
import { clearAuthSessionCookies } from "@/lib/authCookies";
import { getPartnerAuthHeaders } from "@/lib/apiAuth";
import { PARTNER_CLIENT_BASE_OPTIONS } from "@/constants/partnerProfileOptions";
import PartnerPageHeader from "../components/PartnerPageHeader";
import PartnerSelect from "@/pages/LandingPages/marketingAgency/shared/PartnerSelect";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_PROFILE = {
  name: "",
  agencyWebsite: "",
  clientBase: "",
  niche: "",
};

const extractErrorMessage = (err, fallback) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  }
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.request && !err?.response) {
    return "Network error. Please check your connection and try again.";
  }
  return fallback;
};

const SettingsSkeleton = () => (
  <div
    className="rounded-2xl border border-partner bg-[#11151F] p-8 animate-pulse space-y-4"
    data-testid="partner-settings-skeleton"
  >
    <div className="h-6 w-40 bg-white/5 rounded" />
    <div className="h-10 bg-white/5 rounded" />
    <div className="h-10 bg-white/5 rounded" />
    <div className="h-10 bg-white/5 rounded" />
  </div>
);

const PartnerSettings = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState(Cookies.get("email") || "");
  const [profile, setProfile] = useState({ ...EMPTY_PROFILE });
  const [profileErrors, setProfileErrors] = useState({});
  const [profileError, setProfileError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordApiError, setPasswordApiError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);

  const getAuthHeader = useCallback(() => getPartnerAuthHeaders(), []);

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const res = await axios.get(`${BACKEND_URL}/api/partner/settings`, {
        headers: getAuthHeader(),
      });
      const data = res.data || {};
      setEmail(data.email || Cookies.get("email") || "");
      setProfile({
        name: data.name || "",
        agencyWebsite: data.agencyWebsite || "",
        clientBase: data.clientBase || "",
        niche: data.niche || "",
      });
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAuthSessionCookies();
        navigate("/login");
        return;
      }
      setLoadError(
        extractErrorMessage(err, "Could not load your partner settings. Please try again.")
      );
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeader, navigate]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const validateProfile = () => {
    const next = {};
    if (!profile.name.trim()) next.name = "Name is required";
    if (!profile.niche.trim()) next.niche = "Niche is required";
    if (!profile.clientBase) next.clientBase = "Select client base size";
    setProfileErrors(next);
    return Object.keys(next).length === 0;
  };

  const validatePassword = () => {
    const next = {};
    if (!currentPassword) next.currentPassword = "Current password is required";
    if (!newPassword) next.newPassword = "New password is required";
    else if (newPassword.length < 6) next.newPassword = "Password must be at least 6 characters";
    if (newPassword !== confirmPassword) next.confirmPassword = "Passwords do not match";
    setPasswordErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!validateProfile() || isSaving) return;

    setIsSaving(true);
    setProfileError("");
    try {
      const res = await axios.patch(
        `${BACKEND_URL}/api/partner/settings`,
        {
          name: profile.name.trim(),
          agencyWebsite: profile.agencyWebsite.trim(),
          clientBase: profile.clientBase,
          niche: profile.niche.trim(),
        },
        { headers: getAuthHeader() }
      );
      const data = res.data || {};
      setProfile({
        name: data.name || "",
        agencyWebsite: data.agencyWebsite || "",
        clientBase: data.clientBase || "",
        niche: data.niche || "",
      });
      setEmail(data.email || email);
      toast.success("Profile saved");
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAuthSessionCookies();
        navigate("/login");
        return;
      }
      setProfileError(
        extractErrorMessage(err, "Could not save profile. Please try again.")
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword() || isPasswordSubmitting) return;

    setIsPasswordSubmitting(true);
    setPasswordApiError("");
    setPasswordSuccess(false);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
          confirm_new_password: confirmPassword,
        },
        { headers: getAuthHeader() }
      );

      const data = response.data;
      if (data.status === "error") {
        setPasswordApiError(data.message || "Failed to change password. Please try again.");
        return;
      }

      setPasswordSuccess(true);
      toast.success("Password updated");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordErrors({});
    } catch (err) {
      if (err?.response?.status === 401) {
        setPasswordApiError("Session expired. Please log in again.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }
      setPasswordApiError(
        extractErrorMessage(err, "Failed to change password. Please try again.")
      );
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  const handleLogout = () => {
    clearAuthSessionCookies();
    navigate("/");
  };

  if (isLoading) {
    return (
      <div data-testid="partner-settings">
        <PartnerPageHeader
          tag="Account"
          title="Partner"
          titleAccent="settings"
          subtitle="Manage your agency profile and credentials."
        />
        <SettingsSkeleton />
      </div>
    );
  }

  if (loadError) {
    return (
      <div data-testid="partner-settings">
        <PartnerPageHeader
          tag="Account"
          title="Partner"
          titleAccent="settings"
          subtitle="Manage your agency profile and credentials."
        />
        <div
          className="rounded-2xl border border-red-500/30 bg-red-500/5 p-8 text-center"
          data-testid="partner-settings-load-error"
        >
          <AlertTriangle className="mx-auto h-10 w-10 text-red-400 mb-4" />
          <p className="text-sm text-partner-muted font-body max-w-md mx-auto mb-4">{loadError}</p>
          <button
            type="button"
            onClick={fetchSettings}
            className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold bg-white/10 text-partner hover:bg-white/20"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="partner-settings">
      <PartnerPageHeader
        tag="Account"
        title="Partner"
        titleAccent="settings"
        subtitle="Manage your agency profile and credentials."
      />

      <section className="rounded-2xl border border-partner bg-[#11151F] p-6 sm:p-8 mb-6">
        <h2 className="text-lg font-bold text-partner mb-6">Partner profile</h2>
        {profileError && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-400">
            {profileError}
          </div>
        )}
        <form onSubmit={handleProfileSave} className="space-y-4 max-w-xl">
          <div>
            <label htmlFor="name" className="block text-xs text-partner-muted font-body mb-1.5">
              Full name
            </label>
            <input
              id="name"
              type="text"
              value={profile.name}
              onChange={(e) => {
                setProfile((p) => ({ ...p, name: e.target.value }));
                setProfileErrors((err) => ({ ...err, name: "" }));
              }}
              disabled={isSaving}
              className="w-full partner-input rounded-lg px-4 py-2.5 text-sm font-body disabled:opacity-50"
            />
            {profileErrors.name && (
              <p className="mt-1 text-xs text-red-400">{profileErrors.name}</p>
            )}
          </div>
          <div>
            <label htmlFor="agencyWebsite" className="block text-xs text-partner-muted font-body mb-1.5">
              Agency website
            </label>
            <input
              id="agencyWebsite"
              type="url"
              value={profile.agencyWebsite}
              onChange={(e) => setProfile((p) => ({ ...p, agencyWebsite: e.target.value }))}
              disabled={isSaving}
              className="w-full partner-input rounded-lg px-4 py-2.5 text-sm font-body disabled:opacity-50"
            />
          </div>
          <div>
            <label htmlFor="clientBase" className="block text-xs text-partner-muted font-body mb-1.5">
              Client base size
            </label>
            <PartnerSelect
              id="clientBase"
              value={profile.clientBase}
              onChange={(e) => {
                setProfile((p) => ({ ...p, clientBase: e.target.value }));
                setProfileErrors((err) => ({ ...err, clientBase: "" }));
              }}
              options={PARTNER_CLIENT_BASE_OPTIONS}
              placeholder="Select range"
              disabled={isSaving}
            />
            {profileErrors.clientBase && (
              <p className="mt-1 text-xs text-red-400">{profileErrors.clientBase}</p>
            )}
          </div>
          <div>
            <label htmlFor="niche" className="block text-xs text-partner-muted font-body mb-1.5">
              Primary niche
            </label>
            <input
              id="niche"
              type="text"
              value={profile.niche}
              onChange={(e) => {
                setProfile((p) => ({ ...p, niche: e.target.value }));
                setProfileErrors((err) => ({ ...err, niche: "" }));
              }}
              disabled={isSaving}
              className="w-full partner-input rounded-lg px-4 py-2.5 text-sm font-body disabled:opacity-50"
            />
            {profileErrors.niche && (
              <p className="mt-1 text-xs text-red-400">{profileErrors.niche}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={isSaving}
            data-testid="partner-settings-save"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#00D2FF] text-[#080A0F] font-semibold text-sm hover:bg-[#00D2FF]/90 transition-colors disabled:opacity-50"
          >
            {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save profile
          </button>
        </form>
      </section>

      <section className="rounded-2xl border border-partner bg-[#11151F] p-6 sm:p-8 mb-6">
        <h2 className="text-lg font-bold text-partner mb-6">Change password</h2>
        {passwordSuccess && (
          <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-400">
            Password changed successfully.
          </div>
        )}
        {passwordApiError && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-400">
            {passwordApiError}
          </div>
        )}
        <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-xl">
          {[
            {
              id: "currentPassword",
              label: "Current password",
              value: currentPassword,
              setValue: setCurrentPassword,
              show: showCurrent,
              setShow: setShowCurrent,
              error: passwordErrors.currentPassword,
            },
            {
              id: "newPassword",
              label: "New password",
              value: newPassword,
              setValue: setNewPassword,
              show: showNew,
              setShow: setShowNew,
              error: passwordErrors.newPassword,
            },
          ].map((field) => (
            <div key={field.id}>
              <label htmlFor={field.id} className="block text-xs text-partner-muted font-body mb-1.5">
                {field.label}
              </label>
              <div className="relative">
                <input
                  id={field.id}
                  type={field.show ? "text" : "password"}
                  value={field.value}
                  onChange={(e) => {
                    field.setValue(e.target.value);
                    setPasswordErrors((err) => ({ ...err, [field.id]: "" }));
                    setPasswordApiError("");
                    setPasswordSuccess(false);
                  }}
                  disabled={isPasswordSubmitting}
                  className="w-full partner-input rounded-lg px-4 py-2.5 pr-10 text-sm font-body disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => field.setShow(!field.show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-partner-muted hover:text-partner"
                  aria-label={field.show ? "Hide password" : "Show password"}
                >
                  {field.show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {field.error && <p className="mt-1 text-xs text-red-400">{field.error}</p>}
            </div>
          ))}
          <div>
            <label htmlFor="confirmPassword" className="block text-xs text-partner-muted font-body mb-1.5">
              Confirm new password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setPasswordErrors((err) => ({ ...err, confirmPassword: "" }));
                setPasswordApiError("");
                setPasswordSuccess(false);
              }}
              disabled={isPasswordSubmitting}
              className="w-full partner-input rounded-lg px-4 py-2.5 text-sm font-body disabled:opacity-50"
            />
            {passwordErrors.confirmPassword && (
              <p className="mt-1 text-xs text-red-400">{passwordErrors.confirmPassword}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={isPasswordSubmitting}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg border border-partner text-partner font-semibold text-sm hover:bg-[#080A0F] transition-colors disabled:opacity-50"
          >
            {isPasswordSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Update password
          </button>
        </form>
      </section>

      <section className="rounded-2xl border border-partner bg-[#11151F] p-6 sm:p-8">
        <h2 className="text-lg font-bold text-partner mb-4">Account</h2>
        <p className="text-sm text-partner-muted font-body mb-1">Email</p>
        <p className="text-sm text-partner font-body mb-6">{email || "Not set"}</p>
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-partner-muted hover:text-red-400 hover:bg-red-500/10 font-body text-sm transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Log out
        </button>
      </section>
    </div>
  );
};

export default PartnerSettings;
